import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  FireflyButton,
  Icon,
  Menu,
  MenuOption,
} from '@equip.health/ui';
import { AssessmentEventAttribute } from '~/analytics/assessmentEvents';

import { SurveyAccordionEvents } from '~/analytics/surveyAccordionEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

const CTA_HEIGHT = '42px';

const TOP_LEVEL_ID = 'patient-survey-compact-navigation';

const {
  typography: { paragraph },
} = EquipUITheme;

const {
  neutral: { tertiary },
} = EquipUIFireflyV2Theme.colors;

type NavigationProps = {
  currentPage: number;
  error: string;
  isFullScreen: boolean;
  isLoading: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: (page: string) => void;
  onSubmit: () => void;
  pages: MenuOption[];
  responseExternalId: string;
  surveyName: string;
};

const Navigation = ({
  currentPage,
  error,
  isFullScreen,
  isLoading,
  onNext,
  onPrevious,
  onSkip,
  onSubmit,
  pages,
  responseExternalId,
  surveyName,
}: NavigationProps) => {
  const { trackEvent } = useAnalytics();
  const isSubmit = currentPage === pages.length - 1;

  const handlePreviousClick = () => {
    trackEvent(SurveyAccordionEvents.surveyAccordionBackButtonClicked, {
      'Current Page': currentPage,
      'Page Name': pages[currentPage]?.node,
      [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
      [AssessmentEventAttribute.SurveyName]: surveyName,
    });
    onPrevious();
  };

  const handleSkipClick = (pageId: string) => {
    const selectedPage = pages.find((page) => page.value === pageId);
    trackEvent(
      SurveyAccordionEvents.surveyAccordionSkipToSectionDropdownClicked,
      {
        'From Page': currentPage,
        'From Page Name': pages[currentPage]?.node,
        'To Page Name': selectedPage?.node,
        [AssessmentEventAttribute.ResponseExternalId]: responseExternalId,
        [AssessmentEventAttribute.SurveyName]: surveyName,
      },
    );
    onSkip(pageId);
  };

  return (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      <ChakraFlex alignItems="center" height="100%">
        {currentPage > 0 && (
          <ChakraBox marginRight="12px">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__back`}
              isDisabled={isLoading}
              leftIcon={FAIconName.chevronLeftRegular}
              onClick={handlePreviousClick}
              variant="secondary"
            >
              Back
            </FireflyButton>
          </ChakraBox>
        )}

        {!isFullScreen && pages.length > 1 && (
          <Menu
            border={`1px solid ${tertiary}`}
            height={CTA_HEIGHT}
            icon={<Icon name={FAIconName.chevronDownRegular} />}
            id={`${TOP_LEVEL_ID}__pages`}
            isDisabled={isLoading}
            listMaxHeight="220px"
            maxWidth="650px"
            onSelect={handleSkipClick}
            options={pages}
            overflow="auto"
            placeholder="Skip to section"
            placeholderFontSize="15px"
          />
        )}
      </ChakraFlex>
      <ChakraFlex alignItems="center">
        {error && (
          <ChakraText
            {...paragraph}
            color="critical.base"
            id={`${TOP_LEVEL_ID}__error`}
            marginRight="16px"
          >
            {error}
          </ChakraText>
        )}
        <FireflyButton
          id={`${TOP_LEVEL_ID}__proceed`}
          isDisabled={isLoading}
          onClick={() => (isSubmit ? onSubmit() : onNext())}
          rightIcon={FAIconName.chevronRightRegular}
        >
          {isSubmit ? 'Submit' : 'Save and continue'}
        </FireflyButton>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default Navigation;
